import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../JobDashboard/JobDetailNew.module.css'; // Reusing styles
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import showToast from '../../components/ShowToast';
import Loader2 from '../../components/loader2';
import PostcodeAutocomplete from '../../components/PostcodeAutocomplete/PostcodeAutocomplete';
import { ArrowLeft, Camera, FileText, CheckCircle, X } from 'lucide-react';

const AddEditBusinessPortfolio = () => {
    const navigate = useNavigate();
    const { portfolioId } = useParams();
    const isEditing = !!portfolioId;

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [postcode, setPostcode] = useState('');
    const [suburb, setSuburb] = useState('');
    const [uploadedImages, setUploadedImages] = useState([]); // { key, url, file, isExisting, id }
    const [filesToRemove, setFilesToRemove] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isEditing) {
            const fetchPortfolio = async () => {
                setIsLoading(true);
                try {
                    const businessId = localStorage.getItem('loggedInBusinessId');
                    const response = await axios.get(`${API_BASE_URL}/api/WorkPortfolio/GetWorkPortfolioDetail?portfolioId=${portfolioId}&businessId=${businessId}`);
                    const data = response.data.model;
                    setTitle(data.title);
                    setDescription(data.description);
                    setPostcode(data.postcode);
                    setSuburb(data.suburb);
                    setUploadedImages(data.mediaUrl.map(m => ({ key: m.id, url: m.url, file: null, isExisting: true, id: m.id })));
                } catch (err) {
                    setError('Failed to fetch portfolio details.');
                    showToast('error', 'Failed to fetch portfolio details.');
                    console.error(err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchPortfolio();
        }
    }, [portfolioId, isEditing]);

    const handleImageUpload = (event) => {
        const files = Array.from(event.target.files);
        const newImages = files.map(file => ({
            key: `new-${file.name}-${Date.now()}`,
            url: URL.createObjectURL(file),
            file: file,
            isExisting: false,
            id: null
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
    };

    const handlePostcodeSelect = (locationObject) => {
        // The component now gives us a structured object
        // { fullAddress, postcode, suburb, state }
        setPostcode(locationObject.fullAddress); // e.g., "2166, Cabramatta"
        setSuburb(locationObject.suburb);       // e.g., "Cabramatta"
    };

    const removeImage = (key, id) => {
        if (isEditing && id) {
            setFilesToRemove(prev => [...prev, id]);
        }
        setUploadedImages(prev => prev.filter(img => img.key !== key));
    };

    const handleSubmit = async () => {
        console.log('handleSubmit triggered');
        console.log('isEditing:', isEditing);

        if (!title || !description || !postcode || !suburb) {
            showToast('error', 'Please fill all fields.');
            console.log('Validation failed: Missing fields', { title, description, postcode, suburb });
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        const businessId = localStorage.getItem('loggedInBusinessId');

        formData.append('businessId', businessId);
        formData.append('title', title);
        formData.append('description', description);

        // TODO - split the postcode here
        formData.append('postcode', postcode);
        
        formData.append('suburb', suburb);
        formData.append('baseUrl', window.location.origin);

        if (isEditing) {
            formData.append('portfolioId', portfolioId);
        }

        uploadedImages.forEach(img => {
            if (img.file) {
                formData.append('Files', img.file);
            }
        });

        if (filesToRemove.length > 0) {
            filesToRemove.forEach(id => formData.append('filesToRemove', id));
        }

        // Log formData entries
        console.log('FormData entries:');
        for (let [key, value] of formData.entries()) {
            console.log(key, value);
        }

        const endpoint = isEditing ? `${API_BASE_URL}/api/WorkPortfolio/UpdateWorkPortfolio` : `${API_BASE_URL}/api/WorkPortfolio/CreateWorkPortfolio`;
        const method = isEditing ? 'put' : 'post';

        console.log(`API call details: Method=${method}, Endpoint=${endpoint}`);

        try {
            await axios[method](endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showToast('success', `Portfolio ${isEditing ? 'updated' : 'created'} successfully!`);
            navigate('/business-portfolios');
        } catch (err) {
            setError(`Failed to ${isEditing ? 'update' : 'create'} portfolio.`);
            showToast('error', `Failed to ${isEditing ? 'update' : 'create'} portfolio.`);
            console.error('API Error:', err);
            if (err.response) {
                console.error('Error response:', err.response.data);
                console.error('Error status:', err.response.status);
                console.error('Error headers:', err.response.headers);
            } else if (err.request) {
                console.error('Error request:', err.request);
            } else {
                console.error('Error message:', err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`${styles.jobDetailNew_minHeightScreen} ${styles.jobDetailNew_bgGradientPage}`}>
            {isLoading && <Loader2 />}
            <header className={styles.jobDetailNew_header}>
                <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_headerContent}`}>
                    <button onClick={() => navigate(-1)} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonGhost}`} style={{ color: 'white' }}>
                        <ArrowLeft className={styles.jobDetailNew_iconMd} />
                    </button>
                    <h1 className={styles.jobDetailNew_headerTitle}>{isEditing ? 'Edit Work Portfolio' : 'Create Work Portfolio'}</h1>
                </div>
            </header>

            <div className={`${styles.jobDetailNew_container} ${styles.jobDetailNew_mainContent}`}>
                <div className={styles.jobDetailNew_card}>
                    <div className={styles.jobDetailNew_cardHeader}>
                        <h3 className={styles.jobDetailNew_cardTitle}>
                            <FileText className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textBlue600}`} />
                            <span>Portfolio Information</span>
                        </h3>
                    </div>
                    <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY6}`}>
                        <div className={`${styles.jobDetailNew_grid} ${styles.jobDetailNew_md_gridCols2}`} style={{ gap: '1rem' }}>
                            <div>
                                <label htmlFor="title" className={styles.jobDetailNew_label}>Title</label>
                                <input id="title" type="text" placeholder="e.g., Modern Kitchen Remodel" value={title} onChange={(e) => setTitle(e.target.value)} className={styles.jobDetailNew_input} />
                            </div>
                            <div>
                                <label htmlFor="postcode" className={styles.jobDetailNew_label}>Postcode</label>
                                
                                <PostcodeAutocomplete 
                                    value={postcode} 
                                    onSuggestionSelect={handlePostcodeSelect} 
                                />

                            </div>
                        </div>
                        <div>
                            <label htmlFor="suburb" className={styles.jobDetailNew_label}>Suburb</label>
                            <input id="suburb" type="text" placeholder="e.g., Sydney" value={suburb} onChange={(e) => setSuburb(e.target.value)} className={styles.jobDetailNew_input} readOnly />
                        </div>
                        <div>
                            <label htmlFor="description" className={styles.jobDetailNew_label}>Description</label>
                            <textarea id="description" placeholder="Describe the project..." value={description} onChange={(e) => setDescription(e.target.value)} className={styles.jobDetailNew_textarea} rows={4}></textarea>
                        </div>
                    </div>
                </div>

                <div className={styles.jobDetailNew_card}>
                    <div className={styles.jobDetailNew_cardHeader}>
                        <h3 className={styles.jobDetailNew_cardTitle}>
                            <Camera className={`${styles.jobDetailNew_iconMd} ${styles.jobDetailNew_textAmber600}`} />
                            <span>Project Images</span>
                        </h3>
                    </div>
                    <div className={`${styles.jobDetailNew_cardContent} ${styles.jobDetailNew_cardContentSpaceY4}`}>
                        <div className={styles.jobDetailNew_uploadArea}>
                            <label htmlFor="file-upload" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonOutline}`}>
                                Browse Files
                                <input id="file-upload" type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
                            </label>
                        </div>
                        {uploadedImages.length > 0 && (
                            <div className={styles.jobDetailNew_uploadedImagesGrid}>
                                {uploadedImages.map((image) => (
                                    <div key={image.key} className={styles.jobDetailNew_uploadedImageItem}>
                                        <img src={image.url} alt="upload preview" className={styles.jobDetailNew_uploadedImage} />
                                        <button type="button" className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_imageRemoveBtn}`} onClick={() => removeImage(image.key, image.id)}>
                                            <X className={styles.jobDetailNew_buttonIcon} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <button type="button" onClick={handleSubmit} className={`${styles.jobDetailNew_button} ${styles.jobDetailNew_buttonFullWidth} ${styles.jobDetailNew_buttonPrimary}`}>
                        <CheckCircle className={`${styles.jobDetailNew_iconBase} ${styles.jobDetailNew_buttonIconMr2}`} />
                        {isEditing ? 'Save Changes' : 'Create Portfolio'}
                    </button>
                </div>
                {error && <p className="text-red-500 mt-4">{error}</p>}
            </div>
        </div>
    );
};

export default AddEditBusinessPortfolio;
